// CPT Evidence Cross-Reference Engine
// Maps clinical severity AI payload against Code Cart CPTs to determine evidence support status

import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import type { CaseCptLine } from '@/lib/templates/types';

export type EvidenceStatus = 'supported' | 'unsupported' | 'override';

export interface CptEvidenceResult {
  code: string;
  status: EvidenceStatus;
  evidenceSummary: string | null;
  /** User override reason (set when surgeon clicks "Manual Override") */
  overrideReason?: string;
}

// Semantic map: CPT code prefixes/ranges → clinical domain keywords
const CPT_EVIDENCE_MAP: Record<string, string[]> = {
  // TKA / Knee replacement
  '27447': ['knee', 'arthroplasty', 'replacement', 'bone-on-bone', 'bone on bone', 'kellgren', 'kl grade', 'degenerative', 'osteoarthritis', 'joint space'],
  '27446': ['knee', 'arthroplasty', 'unicompartmental', 'partial replacement'],
  '27130': ['hip', 'arthroplasty', 'replacement'],
  // Knee arthroscopy
  '29881': ['meniscectomy', 'meniscus', 'tear', 'arthroscopy', 'medial', 'lateral'],
  '29880': ['meniscectomy', 'meniscus', 'repair'],
  '29877': ['chondroplasty', 'chondral', 'cartilage', 'grade', 'outerbridge'],
  '29876': ['chondroplasty', 'chondral', 'cartilage', 'debridement'],
  '29874': ['loose body', 'loose bodies', 'removal'],
  '29875': ['synovectomy', 'synovitis', 'synovial'],
  // Shoulder
  '29827': ['rotator cuff', 'supraspinatus', 'infraspinatus', 'repair', 'tear', 'shoulder'],
  '29826': ['acromioplasty', 'subacromial', 'decompression', 'shoulder'],
  '29824': ['clavicle', 'distal clavicle', 'acromioclavicular', 'ac joint'],
  '29823': ['debridement', 'shoulder', 'extensive'],
  '29807': ['slap', 'labrum', 'labral', 'superior labrum', 'biceps anchor', 'shoulder'],
  '23420': ['rotator cuff', 'supraspinatus', 'subacromial', 'shoulder', 'open repair'],
  '23430': ['biceps', 'tenodesis', 'long-head', 'long head', 'shoulder'],
  '23120': ['distal clavicle', 'acromioclavicular', 'ac joint', 'arthrosis', 'shoulder'],
  '29828': ['biceps', 'tenodesis', 'long-head', 'long head', 'shoulder'],
  '23472': ['shoulder', 'replacement', 'arthroplasty'],
  '23470': ['shoulder', 'hemiarthroplasty'],
  // Injection
  '20610': ['injection', 'aspiration', 'joint injection'],
  '20611': ['injection', 'aspiration'],
  // Surgical assistant (always supported — administrative)
  '09980': [],
};

/**
 * Build a searchable text corpus from clinical severity data
 */
function buildClinicalCorpus(data: ClinicalSeverityData): string {
  const parts: string[] = [];

  // Radiographic findings
  if (data.radiographic_findings) {
    const rf = data.radiographic_findings;
    if (rf.imaging_modality) parts.push(rf.imaging_modality);
    if (rf.severity_grade) parts.push(rf.severity_grade);
    if (rf.specific_findings) parts.push(...rf.specific_findings);
  }

  // Conservative therapy
  if (data.conservative_therapy) {
    data.conservative_therapy.forEach(ct => {
      parts.push(ct.modality);
      if (ct.duration) parts.push(ct.duration);
      if (ct.outcome) parts.push(ct.outcome);
    });
  }

  // Injection history
  if (data.injection_history) {
    data.injection_history.forEach(inj => {
      parts.push(inj.type);
      if (inj.location) parts.push(inj.location);
      if (inj.outcome) parts.push(inj.outcome);
    });
  }

  // Functional limitations
  if (data.functional_limitations) {
    parts.push(...data.functional_limitations);
  }

  // Pain assessment
  if (data.pain_assessment) {
    if (data.pain_assessment.pain_character) parts.push(data.pain_assessment.pain_character);
    if (data.pain_assessment.pain_duration) parts.push(data.pain_assessment.pain_duration);
  }

  // ROM measurements
  if (data.rom_measurements) {
    if (data.rom_measurements.flexion) parts.push(`flexion ${data.rom_measurements.flexion}`);
    if (data.rom_measurements.extension) parts.push(`extension ${data.rom_measurements.extension}`);
    if (data.rom_measurements.other_measurements) parts.push(...data.rom_measurements.other_measurements);
  }

  // Comorbidities
  if (data.comorbidities) {
    parts.push(...data.comorbidities);
  }

  return parts.join(' ').toLowerCase();
}

/**
 * Cross-reference CPT codes against clinical severity data.
 * Returns evidence status for each CPT line.
 */
export function crossReferenceCptEvidence(
  cptLines: CaseCptLine[],
  clinicalData: ClinicalSeverityData | null | undefined,
  existingOverrides?: Record<string, string>,
): CptEvidenceResult[] {
  // If no clinical data, all codes are "unsupported" (no evidence to check)
  // BUT we treat this as "no data available" — don't flag anything
  if (!clinicalData) {
    return cptLines.map(line => ({
      code: String(line.code),
      status: 'supported' as EvidenceStatus,
      evidenceSummary: null,
    }));
  }

  const corpus = buildClinicalCorpus(clinicalData);

  return cptLines.map(line => {
    const code = String(line.code);
    // Check for existing manual override
    if (existingOverrides?.[code]) {
      return {
        code,
        status: 'override' as EvidenceStatus,
        evidenceSummary: null,
        overrideReason: existingOverrides[code],
      };
    }

    // Administrative codes always supported
    if (code === '09980') {
      return {
        code,
        status: 'supported' as EvidenceStatus,
        evidenceSummary: 'Administrative code — no clinical evidence required.',
      };
    }

    // Look up keywords for this CPT
    const keywords = CPT_EVIDENCE_MAP[code];

    if (keywords && keywords.length > 0) {
      // Check how many keywords match
      const matched = keywords.filter(kw => corpus.includes(kw));
      const matchRatio = matched.length / keywords.length;

      if (matchRatio >= 0.2 || matched.length >= 2) {
        return {
          code,
          status: 'supported' as EvidenceStatus,
          evidenceSummary: `Evidence found: ${matched.slice(0, 3).join(', ')}`,
        };
      }

      return {
        code,
        status: 'unsupported' as EvidenceStatus,
        evidenceSummary: `No clinical evidence found for ${line.description}. Keywords checked: ${keywords.slice(0, 4).join(', ')}.`,
      };
    }

    // Unknown CPT — use description-based heuristic
    const descWords = line.description.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    const hasDescEvidence = descWords.some(w => corpus.includes(w));

    return {
      code,
      status: hasDescEvidence ? 'supported' as EvidenceStatus : 'unsupported' as EvidenceStatus,
      evidenceSummary: hasDescEvidence
        ? `Evidence matched via description keywords.`
        : `No clinical evidence found matching "${line.description}".`,
    };
  });
}

/**
 * Filter CPT lines to only include supported + overridden codes.
 * Used to lock down the payload sent to generate-rcm-narrative.
 */
export function filterSupportedCptLines(
  cptLines: CaseCptLine[],
  evidenceResults: CptEvidenceResult[],
): CaseCptLine[] {
  const allowedCodes = new Set(
    evidenceResults
      .filter(r => r.status === 'supported' || r.status === 'override')
      .map(r => r.code)
  );
  return cptLines.filter(line => allowedCodes.has(String(line.code)));
}
